import React from 'react'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const PurchaseFlow = () => (
  <>
    <Card gap='$4'>
      <Paragraph size='$5'>
        When an Event has paid{' '}
        <Link href='/docs/events/tiers'>ticketing tiers</Link>, attendees see a{' '}
        <Paragraph size='$5' fontWeight='$7'>Checkout</Paragraph> button on the{' '}
        <Link href='/docs/events/page'>Event page</Link>. Here is what they
        experience during the purchase process.
      </Paragraph>
      <DocScreenshot
        src='docs/events/event-page.png'
        alt='Event page with Checkout button that attendees click to begin the ticket purchase flow'
        caption='Attendees click Checkout on the Event page to start the purchase flow'
      />
    </Card>

    <Card gap='$4'>
      <DocSection id='purchase-steps' title='Purchase Steps'>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='ticket-diagonal' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Selecting a tier</Paragraph> —
              Attendees choose from the available ticketing tiers. Each tier shows its
              name, description, and price. If multiple tiers are available (e.g.,
              General Admission and VIP), attendees can compare options before
              selecting.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='person' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Entering attendee details</Paragraph>{' '}
              — For each ticket, the purchaser enters the attendee's name and email
              address. They can use the{' '}
              <Paragraph size='$5' fontWeight='$7'>For me</Paragraph> option to
              quickly assign a ticket to themselves, or enter different details to
              purchase tickets on behalf of others — common for companies buying for
              employees or friends buying as a group.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='ribbon-filled' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Applying discount codes</Paragraph>{' '}
              — If a{' '}
              <Link href='/docs/events/discounts'>discount code</Link> is available,
              attendees can enter it during checkout. The discount is reflected in the
              payment breakdown, showing the subtotal, discount amount, any applicable
              fees and tax, and the final total.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='money' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Payment</Paragraph> — Attendees
              enter their payment details through Stripe's secure payment form. Payment
              processing is handled entirely by Stripe, so card details never touch
              Guild's servers.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='mail' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Confirmation and claiming</Paragraph>{' '}
              — After a successful payment, each ticket recipient receives a{' '}
              <Paragraph size='$5' fontWeight='$7'>claim email</Paragraph>. The
              recipient clicks the claim link, signs in or creates an account, and the
              ticket is linked to their profile. Claiming a ticket automatically
              creates the recipient's Event attendance, so there is no need to RSVP
              separately.
            </Paragraph>
          </XStack>
        </YStack>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='after-purchase' title='After Purchase'>
        <Paragraph size='$5'>
          The Event page also displays attendee profile photos after they claim
          their tickets, giving visitors a quick sense of who has already secured
          a spot.
        </Paragraph>
      </DocSection>
    </Card>
  </>
)

export default PurchaseFlow
